import { Card, HStack, Stack, Text } from '@chakra-ui/react'
import { ReferenceLine } from 'recharts'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@/ui/components/chart/rc-theme'
import { toCssVar } from '@/theme/foundations/colors'
import { SERVICE_COLOR, serviceLabel } from './serviceDisplay'
import type { SibServiceCode } from './useSibCapacityOverview'
import type { ServiceProgress, WorkProgress } from './workProgress'

const TARGET_NEUTRAL = 'fg.muted'
const DASH = { target: '6 4', projection: '2 4' }
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
const percent = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

type Props = {
  progress: WorkProgress
  /** null = all services with an order ("Übersicht"). */
  service: SibServiceCode | null
  showProjection: boolean
}

type LineSpec = {
  key: string
  name: string
  color: string
  kind: 'target' | 'completed' | 'projection'
}

/** First day index of each month, for the x-axis ticks. */
function monthStarts(year: number): number[] {
  return MONTHS.map((_, m) =>
    Math.round((Date.UTC(year, m, 1) - Date.UTC(year, 0, 1)) / 86_400_000)
  )
}

function dateLabel(year: number, day: number) {
  const d = new Date(Date.UTC(year, 0, 1 + day))
  return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${year}`
}

function LineSwatch({ color, dash }: { color: string; dash?: string }) {
  return (
    <svg width="24" height="10" aria-hidden="true">
      <line
        x1="1"
        y1="5"
        x2="23"
        y2="5"
        stroke={toCssVar(color)}
        strokeWidth="2"
        strokeDasharray={dash}
        strokeLinecap="round"
      />
    </svg>
  )
}

export function WorkProgressChart({ progress, service, showProjection }: Props) {
  const shown: ServiceProgress[] = progress.services.filter(
    s => s.ordered > 0 && (service == null || s.code === service)
  )

  const lines: LineSpec[] = shown.flatMap(s => {
    const name = serviceLabel(s.code)
    const color = SERVICE_COLOR[s.code]
    const specs: LineSpec[] = [
      {
        key: `${s.code}_target`,
        name: `Soll ${name}`,
        color: service ? TARGET_NEUTRAL : color,
        kind: 'target',
      },
      { key: `${s.code}_completed`, name: `Geleistet ${name}`, color, kind: 'completed' },
    ]
    if (showProjection && progress.todayIndex != null) {
      specs.push({
        key: `${s.code}_projection`,
        name: `Prognose ${name}`,
        color,
        kind: 'projection',
      })
    }
    return specs
  })

  const rows = Array.from({ length: progress.days }, (_, day) => {
    const row: Record<string, number | null> = { day }
    for (const s of shown) {
      row[`${s.code}_target`] = s.target[day]
      row[`${s.code}_completed`] = s.completed[day]
      row[`${s.code}_projection`] = s.projection[day]
    }
    return row
  })

  // Nothing is capped: the axis grows when work runs beyond the order.
  const highest = Math.max(
    100,
    ...rows.flatMap(r => lines.map(l => (r[l.key] as number | null) ?? 0))
  )
  const yMax = Math.ceil(highest / 20) * 20
  const yTicks = Array.from({ length: yMax / 20 + 1 }, (_, i) => i * 20)

  return (
    <Stack gap="stack">
      <HStack gap="inline" wrap="wrap" aria-label="Legende">
        {shown.map(s => (
          <HStack key={s.code} gap="inline">
            <LineSwatch color={SERVICE_COLOR[s.code]} />
            <Text textStyle="metadata">{serviceLabel(s.code)}</Text>
          </HStack>
        ))}
        <HStack gap="inline">
          <LineSwatch color={TARGET_NEUTRAL} dash={DASH.target} />
          <Text textStyle="metadata">Soll laut Bestellungen</Text>
        </HStack>
        {showProjection && progress.todayIndex != null && (
          <HStack gap="inline">
            <LineSwatch color={TARGET_NEUTRAL} dash={DASH.projection} />
            <Text textStyle="metadata">Prognose (Schätzung)</Text>
          </HStack>
        )}
      </HStack>

      <div
        role="figure"
        aria-label={`Fortschritt ${progress.year} in Prozent der bestellten Leistungen`}
      >
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={rows} margin={{ top: 24, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="border" vertical={false} />
            <XAxis
              dataKey="day"
              type="number"
              domain={[0, progress.days - 1]}
              ticks={monthStarts(progress.year)}
              tickFormatter={(d: number) => MONTHS[monthStarts(progress.year).indexOf(d)] ?? ''}
              stroke="border"
              tick={{ fill: 'fg.muted' }}
            />
            <YAxis
              domain={[0, yMax]}
              ticks={yTicks}
              tickFormatter={(v: number) => `${v} %`}
              stroke="border"
              tick={{ fill: 'fg.muted' }}
              width={56}
            />
            {progress.todayIndex != null && (
              <ReferenceLine
                x={progress.todayIndex}
                stroke={toCssVar('border.emphasized')}
                label={{ value: 'Heute', position: 'top', fill: toCssVar('fg.muted') }}
              />
            )}
            <Tooltip
              content={({ active, label, payload }) =>
                active && payload?.length ? (
                  <Card.Root>
                    <Card.Body>
                      <Card.Description>
                        {dateLabel(progress.year, Number(label))}
                      </Card.Description>
                      <Stack gap="inline">
                        {payload
                          .filter(p => p.value != null)
                          .map(p => (
                            <Text key={String(p.dataKey)} textStyle="metadata">
                              {p.name}: {percent.format(Number(p.value))} %
                            </Text>
                          ))}
                      </Stack>
                    </Card.Body>
                  </Card.Root>
                ) : null
              }
            />
            {lines.map(l => (
              <Line
                key={l.key}
                dataKey={l.key}
                name={l.name}
                type="linear"
                stroke={l.color}
                strokeWidth={2}
                strokeDasharray={l.kind === 'completed' ? undefined : DASH[l.kind]}
                dot={false}
                activeDot={l.kind === 'completed' ? { r: 4 } : false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Stack>
  )
}
